import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolImportBinding, SymbolImportKind } from '../../types.js';
import { pushBinding, stringLiteralText } from './shared.js';

function exportKindForSpecifier(specifier: SyntaxNode, statementKind: SymbolImportKind): SymbolImportKind {
  if (specifier.children.some((child) => child.type === 'type')) return 'type';
  return statementKind;
}

function collectFromExportSpecifier(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  moduleSpecifier: string,
  exportKind: SymbolImportKind,
  specifier: SyntaxNode,
): void {
  const nameNode = specifier.childForFieldName('name');
  const aliasNode = specifier.childForFieldName('alias');
  if (!nameNode) return;
  const sourceName = nameNode.text;
  const localName = aliasNode?.text ?? sourceName;
  const effectiveKind = exportKindForSpecifier(specifier, exportKind);
  pushBinding(out, filePath, workspaceFiles, moduleSpecifier, localName, sourceName, effectiveKind, aliasNode ?? nameNode);
}

function moduleSpecifierFromExport(node: SyntaxNode): string | null {
  const source = node.childForFieldName('source');
  if (!source) return null;
  return stringLiteralText(source);
}

function isStarReexport(node: SyntaxNode): boolean {
  if (!moduleSpecifierFromExport(node)) return false;
  if (node.descendantsOfType('export_specifier').length > 0) return false;
  if (node.descendantsOfType('namespace_export').length > 0) return false;
  if (node.childForFieldName('declaration')) return false;
  return node.text.includes('*');
}

/** Collect re-export bindings from `export … from 'module'` statements. */
export function extractEcmaScriptReexportBindings(
  root: SyntaxNode,
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
): SymbolImportBinding[] {
  const out: SymbolImportBinding[] = [];
  for (const node of root.descendantsOfType('export_statement')) {
    const moduleSpecifier = moduleSpecifierFromExport(node);
    if (!moduleSpecifier) continue;

    const isTypeExport = node.children.some((child) => child.type === 'type');
    const exportKind: SymbolImportKind = isTypeExport ? 'type' : 'reexport';

    if (isStarReexport(node)) {
      const anchor = node.childForFieldName('source') ?? node;
      pushBinding(out, filePath, workspaceFiles, moduleSpecifier, '*', '*', 'star', anchor);
      continue;
    }

    for (const spec of node.descendantsOfType('export_specifier')) {
      collectFromExportSpecifier(out, filePath, workspaceFiles, moduleSpecifier, exportKind, spec);
    }

    for (const ns of node.descendantsOfType('namespace_export')) {
      const alias = ns.childForFieldName('name') ?? ns.namedChildren.find((n) => n.type === 'identifier');
      if (alias) {
        pushBinding(out, filePath, workspaceFiles, moduleSpecifier, alias.text, alias.text, 'namespace', alias);
      }
    }
  }
  return out;
}
